import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaCloudUploadAlt, FaWallet, FaMapMarkerAlt, FaBirthdayCake, FaClock, FaPhoneAlt, FaCheckCircle, FaDownload } from "react-icons/fa";
import APIURL from "../../common/Config";
import "./profile.css";
import defaultProfileImg from "../../assets/blankprofile.webp";
import Footer from "../footer/footer";
import Header from "../header/header";

const ProfilePage = () => {
    const [user, setUser] = useState(null);
    const [editUser, setEditUser] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [walletBalance, setWalletBalance] = useState(0);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);

    useEffect(() => {
        const fetchProfileData = async () => {
            const mobileNumber = localStorage.getItem("phoneNumber");
            const customerId = localStorage.getItem("customerId");
            if (!mobileNumber) { navigate("/"); return; }
            try {
                const [profileRes, balanceRes] = await Promise.all([
                    axios.get(`${APIURL}/astroservice/login/ascustomer/${mobileNumber}`),
                    axios.get(`${APIURL}/wallet/balance/${customerId}`)
                ]);
                setUser(profileRes.data);
                setEditUser(profileRes.data);
                setWalletBalance(balanceRes.data.walletBalance || 0);
            } catch (error) { console.error("Failed to fetch profile:", error); }
        };
        fetchProfileData();
    }, [navigate]);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await axios.get(`${APIURL}/api/country/countryList`);
                setCountries(response.data.map(c => ({ id: c.id, name: c.country_name })));
            } catch (err) { console.error(err); }
        };
        fetchCountries();
    }, []);

    // Handle Change with Strict Limits
    const handleChange = (field, value) => {
        if (field === "pinCode") {
            // Only allow numbers and max 6 digits
            const val = value.replace(/\D/g, "");
            if (val.length > 6) return;
            setEditUser(prev => ({ ...prev, [field]: val }));
        } 
        else if (field === "dob") {
            // Auto-format dd/mm/yyyy and max 10 chars
            let val = value.replace(/\D/g, "");
            if (val.length > 8) return;
            
            let formatted = val;
            if (val.length > 2 && val.length <= 4) {
                formatted = `${val.slice(0, 2)}/${val.slice(2)}`;
            } else if (val.length > 4) {
                formatted = `${val.slice(0, 2)}/${val.slice(2, 4)}/${val.slice(4, 8)}`;
            }
            setEditUser(prev => ({ ...prev, [field]: formatted }));
        } 
        else {
            setEditUser(prev => ({ ...prev, [field]: value }));
        }

        if (errors[field]) setErrors(prev => ({ ...prev, [field]: "" }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEditUser(prev => ({ ...prev, photoUrlpath: URL.createObjectURL(file), photoFile: file }));
        }
    };

    const validateForm = () => {
        let newErrors = {};
        if (!editUser.firstName?.trim()) newErrors.firstName = "Name is required";
        if (!editUser.email?.trim()) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(editUser.email)) newErrors.email = "Invalid email";
        
        // Final strict regex check for dd/mm/yyyy
        const dobRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
        if (!editUser.dob) newErrors.dob = "DOB is required";
        else if (!dobRegex.test(editUser.dob)) newErrors.dob = "Invalid format (dd/mm/yyyy)";

        if (!editUser.pinCode) newErrors.pinCode = "Pincode is required";
        else if (editUser.pinCode.toString().length !== 6) newErrors.pinCode = "Pincode must be 6 digits";

        if (!editUser.timeOfBirth) newErrors.timeOfBirth = "Time is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleUpdate = async () => {
        if (!validateForm()) {
            const firstErr = Object.values(errors)[0];
            if(firstErr) alert(firstErr);
            return;
        }
        setLoading(true);
        try {
            const formData = new FormData();
            const requestData = {
                ...editUser,
                id: editUser.id?.toString(),
                pinCode: editUser.pinCode?.toString(),
                mobile: editUser.mobile?.toString(),
            };
            delete requestData.photoFile;
            
            formData.append("updCustomer", JSON.stringify(requestData));
            if (editUser.photoFile) formData.append("photoUrlpath", editUser.photoFile);
            
            await axios.post(`${APIURL}/astroservice/update/customer`, formData);
            alert("Profile updated successfully!");
            setIsEditing(false);
            window.location.reload();
        } catch (error) { alert("Failed to update profile."); } 
        finally { setLoading(false); }
    };

    if (!user) return <div className="cosmic-loader">Loading Your Destiny...</div>;  

    return (
        <div className="profile-page-wrapper">
            <Header />
            <div className="profile-main-container">
                <div className="profile-header-card">
                    <div className="header-flex">
                        <div className="profile-avatar-wrapper">
                            <img src={editUser.photoUrlpath || defaultProfileImg} alt="Profile" className="main-avatar-img" />
                            <button className="cam-btn" onClick={() => fileInputRef.current.click()}><FaCloudUploadAlt /></button>
                            <input type="file" ref={fileInputRef} hidden onChange={handleImageChange} />
                        </div>
                        <div className="profile-name-details">
                            <div className="name-row">
                                <h1>{user.firstName}</h1>
                                <div className="badges">
                                    <span className="verified-badge"><FaCheckCircle /> Verified</span>
                                </div>
                            </div>
                            <div className="stats-row">
                                <div className="stat"><label>Gender</label><span>{user.gender}</span></div>
                                <div className="stat"><label>City</label><span>{user.birthCity}</span></div>
                                <div className="stat wallet-highlight"><label>Wallet</label><span>₹{walletBalance}</span></div>
                                <button className={`edit-mode-btn ${isEditing ? 'cancel' : ''}`} onClick={() => setIsEditing(!isEditing)}>
                                    {isEditing ? "Cancel" : "✏️ Edit Profile"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="profile-details-grid">
                    <div className="detail-card contact-card">
                        <h3><FaPhoneAlt className="gold-icon" /> Contact Information</h3>
                        <div className="card-content">
                            <div className="field-group"><label>Mobile</label><p className="readonly-val">+91-{user.mobile}</p></div>
                            <div className="field-group">
                                <label>Email</label>
                                {isEditing ? <input value={editUser.email || ""} onChange={(e) => handleChange('email', e.target.value)} /> : <p>{user.email}</p>}
                                {errors.email && <span className="err-txt">{errors.email}</span>}
                            </div>
                        </div>
                    </div>

                    <div className="detail-card birth-card">
                        <h3><FaBirthdayCake className="gold-icon" /> Birth Details</h3>
                        <div className="card-content grid-split">
                            <div className="field-group">
                                <label>DOB (DD/MM/YYYY)</label>
                                {isEditing ? (
                                    <input 
                                        placeholder="DD/MM/YYYY" 
                                        value={editUser.dob || ""} 
                                        onChange={(e) => handleChange('dob', e.target.value)} 
                                    />
                                ) : <p>{user.dob}</p>}
                                {errors.dob && <span className="err-txt">{errors.dob}</span>}
                            </div>
                            <div className="field-group">
                                <label>TOB</label>
                                {isEditing ? <input type="time" value={editUser.timeOfBirth || ""} onChange={(e) => handleChange('timeOfBirth', e.target.value)} /> : <p>{user.timeOfBirth}</p>}
                                {errors.timeOfBirth && <span className="err-txt">{errors.timeOfBirth}</span>}
                            </div>
                            <div className="field-group full"><label>Birth Place</label>{isEditing ? <input value={editUser.birthPlace || ""} onChange={(e) => handleChange('birthPlace', e.target.value)} /> : <p>{user.birthPlace}</p>}</div>
                        </div>
                    </div>

                    <div className="detail-card wallet-card dark-wallet-card">
                        <div className="wallet-content">
                            <FaWallet className="wallet-bg-icon" />
                            <h4 className="premium-white">Quick Wallet</h4>
                            <p className="premium-gray">Available Balance</p>
                            <div className="big-balance"><span className="curr">₹</span><span className="val">{walletBalance}</span></div>
                            <button className="recharge-now-btn" onClick={() => navigate('/wallet')}>RECHARGE</button>
                        </div>
                    </div>

                    <div className="detail-card address-card">
                        <h3><FaMapMarkerAlt className="gold-icon" /> Residential Address</h3>
                        <div className="card-content grid-split">
                            <div className="field-group">
                                <label>Pincode</label>
                                {isEditing ? (
                                    <input 
                                        type="text" 
                                        placeholder="6 Digit Pincode"
                                        value={editUser.pinCode || ""} 
                                        onChange={(e) => handleChange('pinCode', e.target.value)} 
                                    />
                                ) : <p>{user.pinCode}</p>}
                                {errors.pinCode && <span className="err-txt">{errors.pinCode}</span>}
                            </div>
                            <div className="field-group"><label>State</label><p>{user.birthState}</p></div>
                            <div className="field-group full">
                                <label>Address</label>
                                <p>{user.birthPlace}, {user.birthCity}</p>
                                <button className="app-download-btn-small" onClick={() => window.open("https://play.google.com/store/apps/details?id=com.astrobhagyap", "_blank")}>
                                    <FaDownload /> Get App
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {isEditing && (
                    <div className="update-sticky-footer animate-slide-up">
                        <button className="final-update-btn" onClick={handleUpdate} disabled={loading}>{loading ? "Updating..." : "Save Profile Changes"}</button>
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
};

export default ProfilePage;